import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Register fonts
try {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxK.woff2', fontWeight: 'normal' },
      { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlfBBc4.woff2', fontWeight: 'bold' },
    ]
  });
} catch (e) {
  console.warn('Font registration failed, using default fonts:', e);
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },

  // ── Header ──────────────────────────────────────────────────────────────────
  header: {
    flexDirection: 'row',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#f59e0b',
    borderBottomStyle: 'solid',
  },
  companySection: { flex: 2 },
  companyName: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#d97706',
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 8.5,
    color: '#6b7280',
    lineHeight: 1.5,
  },
  invoiceSection: { flex: 1, alignItems: 'flex-end' },
  invoiceTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#667eea',
    marginBottom: 8,
  },
  invoiceNumber: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 3,
  },
  invoiceDate: { fontSize: 9, color: '#6b7280' },

  // ── Customer / Info ──────────────────────────────────────────────────────────
  customerSection: {
    flexDirection: 'row',
    marginBottom: 14,
    backgroundColor: '#eff6ff',
    padding: 12,
    borderRadius: 8,
  },
  billToSection: { flex: 1 },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#667eea',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  customerName: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 4,
  },
  customerDetails: {
    fontSize: 8.5,
    color: '#4b5563',
    lineHeight: 1.5,
  },

  // ── Event Details band ────────────────────────────────────────────────────────
  eventBand: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    backgroundColor: '#f5f3ff',
    padding: 10,
    borderRadius: 8,
    marginBottom: 14,
    borderLeftWidth: 3,
    borderLeftColor: '#667eea',
    borderLeftStyle: 'solid',
  },
  eventItem: {
    width: '25%',
    marginBottom: 4,
  },
  eventLabel: { fontSize: 7.5, color: '#9ca3af' },
  eventValue: { fontSize: 8.5, fontWeight: 'bold', color: '#374151' },

  // ── Items Table ───────────────────────────────────────────────────────────────
  table: { marginBottom: 16 },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#667eea',
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  tableHeaderText: {
    color: '#ffffff',
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    paddingVertical: 7,
    paddingHorizontal: 4,
    minHeight: 28,
  },
  tableRowAlt: { backgroundColor: '#eff6ff' },
  tableCell: { fontSize: 8.5, color: '#374151', textAlign: 'center', paddingHorizontal: 2 },
  tableCellLeft: { textAlign: 'left' },
  tableCellRight: { textAlign: 'right' },

  // Column widths
  cSno:   { width: '4%' },
  cItem:  { width: '24%' },
  cQty:   { width: '6%' },
  cDays:  { width: '7%' },
  cRate:  { width: '11%' },
  cDisc:  { width: '10%' },
  cTax:   { width: '10%' },
  cCgst:  { width: '8%' },
  cSgst:  { width: '8%' },
  cTotal: { width: '12%' },

  // ── Summary ───────────────────────────────────────────────────────────────────
  summarySection: {
    flexDirection: 'row',
    marginTop: 8,
  },
  summaryLeft: { flex: 1, paddingRight: 10 },
  summaryRight: {
    width: 195,
    backgroundColor: '#eff6ff',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#c7d2fe',
    borderStyle: 'solid',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  summaryLabel: { fontSize: 8.5, color: '#6b7280' },
  summaryValue: { fontSize: 8.5, color: '#374151', fontWeight: 'bold' },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 7,
    paddingTop: 7,
    borderTopWidth: 1,
    borderTopColor: '#c7d2fe',
    borderTopStyle: 'solid',
  },
  totalLabel: { fontSize: 11, fontWeight: 'bold', color: '#667eea' },
  totalValue: { fontSize: 11, fontWeight: 'bold', color: '#dc2626' },

  // Remarks box
  remarksBox: {
    marginTop: 10,
    backgroundColor: '#f9fafb',
    padding: 10,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#d1d5db',
    borderLeftStyle: 'solid',
  },
  remarksTitle: { fontSize: 9, fontWeight: 'bold', color: '#374151', marginBottom: 3 },
  remarksText: { fontSize: 8.5, color: '#4b5563', lineHeight: 1.5 },

  // ── Footer ────────────────────────────────────────────────────────────────────
  footer: {
    marginTop: 24,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    borderTopStyle: 'solid',
  },
  footerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  footerColumn: { flex: 1, paddingHorizontal: 8 },
  footerTitle: { fontSize: 9.5, fontWeight: 'bold', color: '#374151', marginBottom: 4 },
  footerText: { fontSize: 8, color: '#6b7280', lineHeight: 1.4 },
  signature: { marginTop: 16, alignItems: 'flex-end' },
  signatureText: { fontSize: 9, color: '#6b7280', marginBottom: 28 },
  signatureLine: {
    width: 150,
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
    borderBottomStyle: 'solid',
    marginBottom: 4,
  },
  signatureLabel: { fontSize: 8, color: '#6b7280', textAlign: 'center' },

  // Watermark
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: 40,
    color: '#eff6ff',
    opacity: 0.15,
    zIndex: -1,
    letterSpacing: 3,
  },

  // Status badge
  statusBadge: {
    position: 'absolute',
    top: 95,
    right: 28,
    backgroundColor: '#667eea',
    color: '#ffffff',
    padding: 7,
    borderRadius: 20,
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
    transform: 'rotate(15deg)',
  },
});

// ────────────────────────────────────────────────────────────────────────────────

const EstimatePDF = ({ estimateData, estimateDetails, companyData }) => {
  const fmtC = (v) => {
    const n = parseFloat(v) || 0;
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtD = (d) => {
    if (!d) return '-';
    try {
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return '-';
      return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '-'; }
  };

  const safe = {
    EstimateNo:         estimateData?.EstimateNo || `EST-${estimateData?.BillNumber || '0001'}`,
    BillDate:           estimateData?.BillDate || new Date().toISOString(),
    CustomerName:       estimateData?.CustomerName || '',
    ContactNumber1:     estimateData?.ContactNumber1 || '',
    ContactNumber2:     estimateData?.ContactNumber2 || '',
    AddressLine1:       estimateData?.AddressLine1 || '',
    AddressLine2:       estimateData?.AddressLine2 || '',
    Location:           estimateData?.Location || '',
    PinCode:            estimateData?.PinCode || '',
    GSTNumber:          estimateData?.GSTNumber || '',
    EnquiryNo:          estimateData?.EnquiryNo || '',
    EventName:          estimateData?.EventName || '',
    EventLocation:      estimateData?.EventLocation || '',
    EventDays:          estimateData?.EventDays || '',
    ProgramDate:        estimateData?.ProgramDate || '',
    DeliveryDate:       estimateData?.DeliveryDate || '',
    DeliveryReturnDate: estimateData?.DeliveryReturnDate || '',
    ExpectedTime:       estimateData?.ExpectedTime || '',
    Amount:             parseFloat(estimateData?.Amount) || 0,
    DiscountAmount:     parseFloat(estimateData?.DiscountAmount) || 0,
    CGSTAmount:         parseFloat(estimateData?.CGSTAmount) || 0,
    SGSTAmount:         parseFloat(estimateData?.SGSTAmount) || 0,
    IGSTAmount:         parseFloat(estimateData?.IGSTAmount) || 0,
    TransportCharge:    parseFloat(estimateData?.TransportingCharge) || 0,
    RoundOff:           parseFloat(estimateData?.RoundOff) || 0,
    TotalAmount:        parseFloat(estimateData?.TotalAmount) || 0,
    AdvanceAmount:      parseFloat(estimateData?.AdvanceAmount) || 0,
    BalanceAmount:      parseFloat(estimateData?.BalanceAmount) || 0,
    Remarks:            estimateData?.Remarks || '',
    Status:             estimateData?.Status || 'Pending',
  };

  const safeItems = Array.isArray(estimateDetails)
    ? estimateDetails.map(item => ({
        ItemName:       item?.ItemName || '',
        Quantity:       parseFloat(item?.Quantity) || 0,
        RentDays:       parseFloat(item?.RentDays) || 0,
        Rate:           parseFloat(item?.Rate) || 0,
        DiscountAmount: parseFloat(item?.DiscountAmount) || 0,
        TaxableAmount:  parseFloat(item?.TaxableAmount) || 0,
        CGSTAmount:     parseFloat(item?.CGSTAmount) || 0,
        SGSTAmount:     parseFloat(item?.SGSTAmount) || 0,
        TotalAmount:    parseFloat(item?.TotalAmount) || 0,
      }))
    : [];

  const statusColor = safe.Status === 'Approved'    ? '#10b981'
                    : safe.Status === 'In Progress'  ? '#f59e0b'
                    : safe.Status === 'Converted'    ? '#667eea'
                    : '#6b7280';

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Watermark */}
        <Text style={styles.watermark}>ESTIMATE</Text>

        {/* Status Badge — hidden (shown via Status field in details only) */}

        {/* ── Header ──────────────────────────────────────────────── */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Text style={styles.companyName}>
              {companyData?.name || 'Your Company Name'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyData?.address || ''}
              {companyData?.addressLine2 ? `\n${companyData.addressLine2}` : ''}
              {companyData?.state ? `\n${companyData.state}` : ''}
              {companyData?.pincode ? ` - ${companyData.pincode}` : ''}{'\n'}
              {companyData?.phone ? `Phone: ${companyData.phone}` : ''}
              {companyData?.email ? `  |  Email: ${companyData.email}` : ''}{'\n'}
              {companyData?.gst ? `GST: ${companyData.gst}` : ''}
            </Text>
          </View>

          <View style={styles.invoiceSection}>
            <Text style={styles.invoiceTitle}>ESTIMATE</Text>
            <Text style={styles.invoiceNumber}># {safe.EstimateNo}</Text>
            <Text style={styles.invoiceDate}>Date: {fmtD(safe.BillDate)}</Text>
            {safe.EnquiryNo ? (
              <Text style={styles.invoiceDate}>Enquiry: {safe.EnquiryNo}</Text>
            ) : null}
          </View>
        </View>

        {/* ── Customer / Details ───────────────────────────────────── */}
        <View style={styles.customerSection}>
          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.customerName}>{safe.CustomerName}</Text>
            <Text style={styles.customerDetails}>
              {safe.AddressLine1 ? `${safe.AddressLine1}\n` : ''}
              {safe.AddressLine2 ? `${safe.AddressLine2}\n` : ''}
              {safe.Location}{safe.PinCode ? ` - ${safe.PinCode}` : ''}
              {(safe.Location || safe.PinCode) ? '\n' : ''}
              {safe.ContactNumber1 ? `Phone: ${safe.ContactNumber1}` : ''}
              {safe.ContactNumber2 ? `  |  ${safe.ContactNumber2}` : ''}
              {safe.GSTNumber ? `\nGST: ${safe.GSTNumber}` : ''}
            </Text>
          </View>

          <View style={styles.billToSection}>
            <Text style={styles.sectionTitle}>Estimate Details</Text>
            <Text style={styles.customerDetails}>
              {`Estimate Date: ${fmtD(safe.BillDate)}\n`}
              {safe.ExpectedTime ? `Expected Time: ${safe.ExpectedTime}\n` : ''}
              {`Status: ${safe.Status}`}
            </Text>
          </View>
        </View>

        {/* ── Event Details band ───────────────────────────────────── */}
        {(safe.EventName || safe.EventLocation || safe.ProgramDate || safe.DeliveryDate) ? (
          <View style={styles.eventBand}>
            {safe.EventName ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Event</Text>
                <Text style={styles.eventValue}>{safe.EventName}</Text>
              </View>
            ) : null}
            {safe.EventLocation ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Venue</Text>
                <Text style={styles.eventValue}>{safe.EventLocation}</Text>
              </View>
            ) : null}
            {safe.ProgramDate ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Program Date</Text>
                <Text style={styles.eventValue}>{fmtD(safe.ProgramDate)}</Text>
              </View>
            ) : null}
            {safe.EventDays ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Event Days</Text>
                <Text style={styles.eventValue}>{safe.EventDays}</Text>
              </View>
            ) : null}
            {safe.DeliveryDate ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Delivery Date</Text>
                <Text style={styles.eventValue}>{fmtD(safe.DeliveryDate)}</Text>
              </View>
            ) : null}
            {safe.DeliveryReturnDate ? (
              <View style={styles.eventItem}>
                <Text style={styles.eventLabel}>Return Date</Text>
                <Text style={styles.eventValue}>{fmtD(safe.DeliveryReturnDate)}</Text>
              </View>
            ) : null}
          </View>
        ) : null}

        {/* ── Items Table ──────────────────────────────────────────── */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.cSno]}>S.No</Text>
            <Text style={[styles.tableHeaderText, styles.cItem, styles.tableCellLeft]}>Item Description</Text>
            <Text style={[styles.tableHeaderText, styles.cQty]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.cDays]}>Days</Text>
            <Text style={[styles.tableHeaderText, styles.cRate]}>Rate</Text>
            <Text style={[styles.tableHeaderText, styles.cDisc]}>Discount</Text>
            <Text style={[styles.tableHeaderText, styles.cTax]}>Taxable</Text>
            <Text style={[styles.tableHeaderText, styles.cCgst]}>CGST</Text>
            <Text style={[styles.tableHeaderText, styles.cSgst]}>SGST</Text>
            <Text style={[styles.tableHeaderText, styles.cTotal]}>Total</Text>
          </View>

          {safeItems.map((item, i) => (
            <View key={i} style={[styles.tableRow, i % 2 === 1 && styles.tableRowAlt]}>
              <Text style={[styles.tableCell, styles.cSno]}>{i + 1}</Text>
              <Text style={[styles.tableCell, styles.cItem, styles.tableCellLeft]}>{item.ItemName}</Text>
              <Text style={[styles.tableCell, styles.cQty]}>{item.Quantity}</Text>
              <Text style={[styles.tableCell, styles.cDays]}>{item.RentDays}</Text>
              <Text style={[styles.tableCell, styles.cRate, styles.tableCellRight]}>{fmtC(item.Rate)}</Text>
              <Text style={[styles.tableCell, styles.cDisc, styles.tableCellRight]}>{fmtC(item.DiscountAmount)}</Text>
              <Text style={[styles.tableCell, styles.cTax, styles.tableCellRight]}>{fmtC(item.TaxableAmount)}</Text>
              <Text style={[styles.tableCell, styles.cCgst, styles.tableCellRight]}>{fmtC(item.CGSTAmount)}</Text>
              <Text style={[styles.tableCell, styles.cSgst, styles.tableCellRight]}>{fmtC(item.SGSTAmount)}</Text>
              <Text style={[styles.tableCell, styles.cTotal, styles.tableCellRight, { fontWeight: 'bold', color: '#059669' }]}>
                {fmtC(item.TotalAmount)}
              </Text>
            </View>
          ))}
        </View>

        {/* ── Summary ─────────────────────────────────────────────── */}
        <View style={styles.summarySection}>
          <View style={styles.summaryLeft}>
            {/* Remarks */}
            {safe.Remarks ? (
              <View style={styles.remarksBox}>
                <Text style={styles.remarksTitle}>Remarks</Text>
                <Text style={styles.remarksText}>{safe.Remarks}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.summaryRight}>
            <View style={styles.summaryRow}>
              <Text style={styles.summaryLabel}>Sub Total:</Text>
              <Text style={styles.summaryValue}>Rs.{fmtC(safe.Amount)}</Text>
            </View>
            {safe.DiscountAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Discount:</Text>
                <Text style={[styles.summaryValue, { color: '#dc2626' }]}>- Rs.{fmtC(safe.DiscountAmount)}</Text>
              </View>
            )}
            {safe.CGSTAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>CGST:</Text>
                <Text style={styles.summaryValue}>Rs.{fmtC(safe.CGSTAmount)}</Text>
              </View>
            )}
            {safe.SGSTAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>SGST:</Text>
                <Text style={styles.summaryValue}>Rs.{fmtC(safe.SGSTAmount)}</Text>
              </View>
            )}
            {safe.IGSTAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>IGST:</Text>
                <Text style={styles.summaryValue}>Rs.{fmtC(safe.IGSTAmount)}</Text>
              </View>
            )}
            {safe.TransportCharge > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Transport / Labour Charge:</Text>
                <Text style={styles.summaryValue}>Rs.{fmtC(safe.TransportCharge)}</Text>
              </View>
            )}
            {safe.RoundOff > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Add. Disc / Round Off:</Text>
                <Text style={[styles.summaryValue, { color: '#dc2626' }]}>- Rs.{fmtC(safe.RoundOff)}</Text>
              </View>
            )}

            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total:</Text>
              <Text style={styles.totalValue}>Rs.{fmtC(safe.TotalAmount)}</Text>
            </View>

            {safe.AdvanceAmount > 0 && (
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Advance Paid:</Text>
                <Text style={[styles.summaryValue, { color: '#10b981' }]}>Rs.{fmtC(safe.AdvanceAmount)}</Text>
              </View>
            )}
            {safe.AdvanceAmount > 0 && (
              <View style={[styles.summaryRow, { marginTop: 4 }]}>
                <Text style={[styles.summaryLabel, { fontWeight: 'bold' }]}>Balance Due:</Text>
                <Text style={[styles.summaryValue, { color: safe.BalanceAmount > 0 ? '#dc2626' : '#10b981', fontSize: 10 }]}>
                  Rs.{fmtC(safe.BalanceAmount)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <View style={styles.footer}>
          <View style={styles.footerSection}>
            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Terms & Conditions</Text>
              <Text style={styles.footerText}>
                {'1. This is an estimate and may vary at the time of final billing.\n'}
                {'2. Items once booked are subject to availability.\n'}
                {'3. Advance payment confirms the booking.\n'}
                {'4. Subject to local jurisdiction only.'}
              </Text>
            </View>

            {(companyData?.bankName || companyData?.accountNo || companyData?.ifscCode) ? (
              <View style={styles.footerColumn}>
                <Text style={styles.footerTitle}>Bank Details</Text>
                <Text style={styles.footerText}>
                  {`Bank: ${companyData?.bankName || '-'}\n`}
                  {`A/C No: ${companyData?.accountNo || '-'}\n`}
                  {`IFSC: ${companyData?.ifscCode || '-'}\n`}
                {`Branch: ${companyData?.branch || '-'}`}
              </Text>
            </View>
            ) : null}

            <View style={styles.footerColumn}>
              <Text style={styles.footerTitle}>Contact</Text>
              <Text style={styles.footerText}>
                {companyData?.email ? `Email: ${companyData.email}\n` : ''}
                {companyData?.phone ? `Phone: ${companyData.phone}\n` : ''}
                {companyData?.website ? `Web: ${companyData.website}` : ''}
              </Text>
            </View>
          </View>

          <View style={styles.signature}>
            <Text style={styles.signatureText}>
              For {companyData?.name || 'Your Company Name'}
            </Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureLabel}>Authorized Signatory</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default EstimatePDF;
